import { toSentences } from '@/common/app/modules/media/citation'
import { CitationBrand, CitationStrategy } from '@/common/model/media/citation'

import Heading from '@/components/view/atoms/heading/Heading'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import PointText from '@/components/view/atoms/text/PointText'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'

import { InsightBox } from '@/components/container/modules/mediaGeo/common/GeoInsightsPanel'
import GeoSection from '@/components/container/modules/mediaGeo/common/GeoSection'

interface Props {
  strategy: CitationStrategy
  brands: CitationBrand[]
}

// 今後のブランディング戦略（AIによる現状の比較分析と強み・弱み）
const CitationStrategyPanel = ({ strategy, brands }: Props) => {
  const ownBrand = brands[0]
  const compBrand = brands[1]
  const comparison = strategy.comparison
  const strengths = strategy.strategy?.strengths
  const weaknesses = strategy.strategy?.weaknesses

  return (
    <GeoSection title='今後のブランディング戦略' noPadding>
      <Heading>1. 現状の比較分析</Heading>
      <div className='flex flex-col gap-4'>
        {comparison && (
          <div className='flex flex-col gap-3'>
            {/* 比較のサマリー */}
            {comparison.overview && (
              <AreaContent>
                <InsideArea color={BgColor.INFO} border>
                  <PointText
                    texts={toSentences(comparison.overview)}
                    size={14}
                    weight='bold'
                    color={TextColor.INFO}
                    lineHeight={150}
                  />
                </InsideArea>
              </AreaContent>
            )}

            {/* 比較項目ごとの対比 */}
            {comparison.table && comparison.table.length > 0 && (
              <table className='border-secondary w-full border-collapse border-b'>
                <thead>
                  <TableRow head>
                    <TableHeadItem type='thin'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        比較項目
                      </Text>
                    </TableHeadItem>
                    <TableHeadItem type='thin' width={320}>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {ownBrand?.name ?? '自社'}
                      </Text>
                    </TableHeadItem>
                    {compBrand && (
                      <TableHeadItem type='thin' width={320}>
                        <Text size={12} color={TextColor.SECONDARY}>
                          {compBrand.name}
                        </Text>
                      </TableHeadItem>
                    )}
                  </TableRow>
                </thead>
                <tbody>
                  {comparison.table.map((row, i) => (
                    <TableRow key={i}>
                      <TableRowItem align='middle'>
                        <Text size={14}>{row.criteria}</Text>
                      </TableRowItem>
                      <TableRowItem width={320} align='middle'>
                        <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
                          {row.own}
                        </Text>
                      </TableRowItem>
                      {compBrand && (
                        <TableRowItem width={320} align='middle'>
                          <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
                            {row.competitor}
                          </Text>
                        </TableRowItem>
                      )}
                    </TableRow>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* 強み・弱み */}
        {(strengths || weaknesses) && (
          <AreaContent>
            <div className='grid grid-cols-1 gap-1 md:grid-cols-2'>
              {strengths && (
                <InsightBox
                  block={{
                    title: '強み',
                    tone: 'positive',
                    texts: strengths.points?.length
                      ? strengths.points
                      : toSentences(strengths.summary)
                  }}
                />
              )}
              {weaknesses && (
                <InsightBox
                  block={{
                    title: '弱み・ギャップ',
                    tone: 'negative',
                    texts: weaknesses.points?.length
                      ? weaknesses.points
                      : toSentences(weaknesses.summary)
                  }}
                />
              )}
            </div>
          </AreaContent>
        )}
      </div>
    </GeoSection>
  )
}

export default CitationStrategyPanel
